// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import {
  Box,
  Button,
  DismissIcon,
  Flex,
  Modal,
  SparkleIcon,
  Typography,
} from '@iheartjane/reefer';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useProductCardContext } from '../productCardProvider';

const VIRTUAL_BUDTENDER_DESCRIPTION_HEIGHT = 96;
const SeeMoreButton = styled.span`
  position: absolute;
  right: 0;
  bottom: 0;
  z-index: 1;
  background-color: white;
  cursor: pointer;
  border: none;
  padding: 0;
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const VirtualBudtenderDescription = () => {
  const { virtualBudtenderDescription } = useProductCardContext();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [hasMaxLinesApplied, setHasMaxLinesApplied] = useState(false);
  const checkOverflow = useCallback(() => {
    if (!containerRef.current) return;
    const isOverflowing =
      containerRef.current.scrollHeight > VIRTUAL_BUDTENDER_DESCRIPTION_HEIGHT;
    setIsOverflowing(isOverflowing);
    // Detect if maxLines CSS is applied using CSS class detection
    const typographyElement =
      containerRef.current.querySelector('[class*="clamped"]');
    if (typographyElement) {
      setHasMaxLinesApplied(
        [...typographyElement.classList].some((c) => c.includes('clamped-line'))
      );
    }
  }, []);
  useEffect(() => {
    checkOverflow();
  }, [virtualBudtenderDescription, checkOverflow]);
  if (!virtualBudtenderDescription) return null;
  return (
    <Flex justifyContent="flex-end" alignItems="flex-end">
      <Flex
        gap={8}
        py={8}
        px={16}
        onClick={() => setShowMore(true)}
        aria-label="Show more virtual budtender description"
      >
        <Box>
          <SparkleIcon color="primary" size="sm" mt={4} />
        </Box>
        <Box
          ref={containerRef}
          position="relative"
          height={VIRTUAL_BUDTENDER_DESCRIPTION_HEIGHT}
        >
          <Typography color="grays-mid" maxLines={4}>
            {virtualBudtenderDescription}
          </Typography>
          {(isOverflowing || hasMaxLinesApplied) && (
            <SeeMoreButton>
              <Typography variant="body">...</Typography>
              <Typography color="primary" variant="body">
                more
              </Typography>
            </SeeMoreButton>
          )}
        </Box>
      </Flex>
      <Modal
        open={showMore}
        appId="root"
        background="grays-white"
        onRequestClose={() => setShowMore(false)}
        overlayClose
        variant="dialogue"
        data-testid="vb-description-modal"
      >
        <Modal.Content
          data-testid="vb-description-modal-content"
          padding={false}
        >
          <Flex p={24} aria-label="virtual budtender description">
            <Box>
              <SparkleIcon color="primary" size="sm" mr={8} mt={4} />
            </Box>
            <Typography color="grays-mid">
              {virtualBudtenderDescription}
            </Typography>
            <Button.Icon
              mt={-16}
              mr={-16}
              onClick={() => setShowMore(false)}
              aria-label="Close virtual budtender description modal"
              data-testid="dismiss-button"
              variant="minimal"
              icon={<DismissIcon color="grays-black" />}
            />
          </Flex>
        </Modal.Content>
      </Modal>
    </Flex>
  );
};
